import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/utils";

/**
 * KpiStatCard — Industrial Display Primitive (F3, TES-950)
 *
 * Flache KPI-Summary-Karte (Stitch screen-05:212-219). 1px outline-variant Kante,
 * KEIN Schatten. Icon-Box w-12 h-12 + tonalem Tint, Label in .text-label-caps,
 * Wert in Headline-Skala (text-2xl font-display — KEIN font-h2-Mock-Token).
 *
 * tone steuert Icon-Box-Tint + Wert-Farbe:
 *   - primary   neutraler Primary-Tint (Default)
 *   - secondary Safety-Yellow (Pending-Metrik, screen-05:226)
 *   - error     destructive (Critical-Metrik, screen-05:235-236)
 */
const kpiIconBoxVariants = cva(
  "w-12 h-12 rounded flex items-center justify-center shrink-0 [&_svg]:size-6",
  {
    variants: {
      tone: {
        primary: "bg-primary/5 text-primary",
        secondary: "bg-secondary/20 text-secondary-foreground",
        error: "bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: {
      tone: "primary",
    },
  },
);

const kpiValueVariants = cva("text-2xl font-display font-semibold leading-tight", {
  variants: {
    tone: {
      primary: "text-foreground",
      secondary: "text-secondary-foreground",
      error: "text-destructive",
    },
  },
  defaultVariants: {
    tone: "primary",
  },
});

interface KpiStatCardProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof kpiIconBoxVariants> {
  /** Icon-Element (z.B. lucide-Icon), wird in der getoenten Box gerendert. */
  icon?: React.ReactNode;
  /** Eyebrow-Label (i18n-Text vom Aufrufer), wird uppercase als .text-label-caps gerendert. */
  label: React.ReactNode;
  /** Kennzahl-Wert (i18n-formatierter String/Node vom Aufrufer). */
  value: React.ReactNode;
}

const KpiStatCard = React.forwardRef<HTMLDivElement, KpiStatCardProps>(
  ({ className, icon, label, value, tone, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "bg-surface-container-lowest p-4 rounded-lg border border-outline-variant flex items-center gap-4",
        className,
      )}
      {...props}
    >
      {icon !== undefined && <div className={cn(kpiIconBoxVariants({ tone }))}>{icon}</div>}
      <div className="min-w-0">
        <p className="text-label-caps text-muted-foreground">{label}</p>
        <p className={cn(kpiValueVariants({ tone }))}>{value}</p>
      </div>
    </div>
  ),
);
KpiStatCard.displayName = "KpiStatCard";

export { KpiStatCard };
